import {
  IPD_CONSCIOUSNESS_LEVELS,
  IPD_DOSE_EARLY_REASON_MINUTES,
  IPD_IV_SITE_CONDITIONS,
  IPD_OXYGEN_DEVICES,
  IPD_READING_TASK_KINDS,
  IPD_TASK_EARLY_LIMIT_MINUTES,
  IPD_TASK_FUTURE_SKEW_MINUTES,
  IPD_TASK_PENDING,
  IPD_TASK_REASONS_BY_OUTCOME,
  ipdReadingProblems,
  ipdTaskIsLate,
  ipdTaskNeedsWitness,
  ipdTaskReadingsSchema,
  ipdTaskStatus,
  type IpdTaskDisplayStatus,
  type IpdTaskKind,
  type IpdTaskOutcome,
  type IpdTaskReadings,
  type IpdTaskReasonCode,
  type IpdTaskView,
  type RecordIpdTaskInput,
} from '@his/shared';
import type { ZodNumber, ZodOptional } from 'zod';

const MINUTE = 60_000;

export type RecordableTask = Pick<IpdTaskView, 'taskKind' | 'scheduledAt' | 'storedStatus' | 'order'>;

export type ConsciousnessLevel = (typeof IPD_CONSCIOUSNESS_LEVELS)[number];
export type IvSiteCondition = (typeof IPD_IV_SITE_CONDITIONS)[number];
export type OxygenDevice = (typeof IPD_OXYGEN_DEVICES)[number];

export const NUMERIC_READINGS = [
  'bpSystolic',
  'bpDiastolic',
  'pulse',
  'respiratoryRate',
  'temperatureC',
  'spo2',
  'painScore',
  'bloodSugarMgDl',
  'flowLpm',
  'fio2',
  'rateMlPerHour',
  'volumeInfusedMl',
] as const;
export type NumericReading = (typeof NUMERIC_READINGS)[number];
export type ReadingField = NumericReading | 'consciousness' | 'siteCondition' | 'device' | 'reaction' | 'reactionNotes';
export type RecordField = ReadingField | 'at' | 'reasonCode' | 'reasonText' | 'witness';

type ReadingTaskKind = 'vitals_round' | 'iv_check' | 'o2_check' | 'transfusion_check';

const READING_FIELDS: Readonly<Record<ReadingTaskKind, readonly ReadingField[]>> = {
  vitals_round: ['bpSystolic', 'bpDiastolic', 'pulse', 'respiratoryRate', 'temperatureC', 'spo2', 'painScore', 'bloodSugarMgDl', 'consciousness'],
  iv_check: ['siteCondition', 'rateMlPerHour', 'volumeInfusedMl'],
  o2_check: ['spo2', 'flowLpm', 'fio2', 'device'],
  transfusion_check: ['temperatureC', 'pulse', 'bpSystolic', 'bpDiastolic', 'respiratoryRate', 'reaction', 'reactionNotes'],
};

const REQUIRED_READINGS: Readonly<Record<ReadingTaskKind, readonly ReadingField[]>> = {
  vitals_round: [],
  iv_check: ['siteCondition'],
  o2_check: ['spo2'],
  transfusion_check: ['temperatureC', 'pulse'],
};

export interface ReadingDraft {
  numbers: Partial<Record<NumericReading, string>>;
  consciousness: ConsciousnessLevel | '';
  siteCondition: IvSiteCondition | '';
  device: OxygenDevice | '';
  reaction: boolean;
  reactionNotes: string;
}

export const EMPTY_READINGS: ReadingDraft = Object.freeze({
  numbers: {},
  consciousness: '',
  siteCondition: '',
  device: '',
  reaction: false,
  reactionNotes: '',
}) as ReadingDraft;

export interface RecordForm {
  outcome: IpdTaskOutcome;
  at: Date;
  reasonCode: IpdTaskReasonCode | '';
  reasonText: string;
  witnessUserId: string;
  doseGiven: string;
  site: string;
  notes: string;
  readings: ReadingDraft;
}

export type RecordProblem = { field: RecordField; key: string; params?: Record<string, string | number> } | { field: RecordField; server: string };

export type ReasonMode = 'required' | 'late' | 'early' | 'none';

export function readingFieldsFor(taskKind: string): readonly ReadingField[] {
  return READING_FIELDS[taskKind as ReadingTaskKind] ?? [];
}

export function readingRequired(taskKind: string, field: ReadingField): boolean {
  return (REQUIRED_READINGS[taskKind as ReadingTaskKind] ?? []).includes(field);
}

export function readingRange(field: NumericReading): { min: number; max: number; integer: boolean } {
  const schema = (ipdTaskReadingsSchema.shape[field] as ZodOptional<ZodNumber>).unwrap();
  return { min: schema.minValue ?? 0, max: schema.maxValue ?? Number.MAX_SAFE_INTEGER, integer: schema.isInt };
}

export function displayStatus(task: Pick<IpdTaskView, 'storedStatus' | 'scheduledAt' | 'taskKind'>, now: number | Date): IpdTaskDisplayStatus {
  return ipdTaskStatus({ status: task.storedStatus, scheduledAt: task.scheduledAt, taskKind: task.taskKind }, now instanceof Date ? now : new Date(now));
}

export function taskIsPending(task: Pick<IpdTaskView, 'storedStatus'>): boolean {
  return task.storedStatus === IPD_TASK_PENDING;
}

export function taskIsStat(task: Pick<IpdTaskView, 'order'>): boolean {
  return task.order.priority === 'stat';
}

export function needsWitness(task: RecordableTask, outcome: IpdTaskOutcome): boolean {
  return outcome === 'given' && ipdTaskNeedsWitness(task.taskKind, task.order);
}

export function witnessNoticeKey(task: RecordableTask): string | null {
  if (!ipdTaskNeedsWitness(task.taskKind, task.order)) return null;
  return task.taskKind === 'dose' ? 'high_alert_medicine_a_second_nurse_must_witness' : 'io_task_high_alert_infusion_witness';
}

export function takesReadings(task: Pick<IpdTaskView, 'taskKind'>, outcome: IpdTaskOutcome): boolean {
  return outcome === 'given' && IPD_READING_TASK_KINDS.includes(task.taskKind as IpdTaskKind);
}

export function takesDose(task: Pick<IpdTaskView, 'taskKind'>, outcome: IpdTaskOutcome): boolean {
  return outcome === 'given' && task.taskKind === 'dose';
}

export function takesSite(task: Pick<IpdTaskView, 'taskKind'>, outcome: IpdTaskOutcome): boolean {
  return outcome === 'given' && (task.taskKind === 'dose' || task.taskKind === 'iv_start');
}

export function takesTime(outcome: IpdTaskOutcome): boolean {
  return outcome !== 'missed';
}

export function recordOutcomes(task: RecordableTask, now: number | Date): IpdTaskOutcome[] {
  const base: IpdTaskOutcome[] = ['given', 'held', 'refused', 'not_given'];
  return displayStatus(task, now) === 'missed' ? [...base, 'missed'] : base;
}

export function minutesEarly(task: Pick<IpdTaskView, 'scheduledAt'>, at: Date): number {
  return (Date.parse(task.scheduledAt) - at.getTime()) / MINUTE;
}

export function doseNeedsEarlyReason(task: RecordableTask, at: Date): boolean {
  return task.taskKind === 'dose' && !taskIsStat(task) && minutesEarly(task, at) > IPD_DOSE_EARLY_REASON_MINUTES;
}

export function tooEarly(task: RecordableTask, at: Date): boolean {
  return !taskIsStat(task) && minutesEarly(task, at) > IPD_TASK_EARLY_LIMIT_MINUTES;
}

export function inFuture(at: Date, now: number | Date): boolean {
  const nowMs = now instanceof Date ? now.getTime() : now;
  return at.getTime() > nowMs + IPD_TASK_FUTURE_SKEW_MINUTES * MINUTE;
}

export function reasonMode(task: RecordableTask, outcome: IpdTaskOutcome, at: Date): ReasonMode {
  if (outcome !== 'given') return 'required';
  if (doseNeedsEarlyReason(task, at)) return 'early';
  if (ipdTaskIsLate(task.taskKind, task.scheduledAt, at)) return 'late';
  return 'none';
}

export function reasonChoices(task: RecordableTask, outcome: IpdTaskOutcome, at: Date): readonly IpdTaskReasonCode[] {
  const mode = reasonMode(task, outcome, at);
  if (mode === 'late') return IPD_TASK_REASONS_BY_OUTCOME.given_late;
  if (mode === 'required' && outcome !== 'given') return IPD_TASK_REASONS_BY_OUTCOME[outcome];
  return [];
}

function numberOf(text: string | undefined): number | null {
  const trimmed = (text ?? '').trim().replace(',', '.');
  if (!trimmed) return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : Number.NaN;
}

export function parseReadings(taskKind: string, draft: ReadingDraft): { readings: IpdTaskReadings; invalid: NumericReading[] } {
  const fields = readingFieldsFor(taskKind);
  const readings: IpdTaskReadings = {};
  const invalid: NumericReading[] = [];
  for (const field of fields) {
    if (!(NUMERIC_READINGS as readonly string[]).includes(field)) continue;
    const numeric = field as NumericReading;
    const value = numberOf(draft.numbers[numeric]);
    if (value === null) continue;
    const range = readingRange(numeric);
    if (Number.isNaN(value) || value < range.min || value > range.max || (range.integer && !Number.isInteger(value))) {
      invalid.push(numeric);
      continue;
    }
    readings[numeric] = value;
  }
  if (fields.includes('consciousness') && draft.consciousness) readings.consciousness = draft.consciousness;
  if (fields.includes('siteCondition') && draft.siteCondition) readings.siteCondition = draft.siteCondition;
  if (fields.includes('device') && draft.device) readings.device = draft.device;
  if (fields.includes('reaction')) {
    readings.reaction = draft.reaction;
    const notes = draft.reactionNotes.trim();
    if (draft.reaction && notes) readings.reactionNotes = notes;
  }
  return { readings, invalid };
}

function readingField(path: string): RecordField {
  const field = path.replace(/^readings\./, '');
  return ((NUMERIC_READINGS as readonly string[]).includes(field) || ['consciousness', 'siteCondition', 'device', 'reaction', 'reactionNotes'].includes(field) ? field : 'pulse') as RecordField;
}

export function recordProblems(task: RecordableTask, form: RecordForm, now: number | Date): RecordProblem[] {
  const problems: RecordProblem[] = [];
  const { outcome, at } = form;
  if (takesTime(outcome) && inFuture(at, now)) problems.push({ field: 'at', key: 'io_task_problem_future' });
  if (outcome === 'given' && tooEarly(task, at)) problems.push({ field: 'at', key: 'io_task_problem_too_early' });
  const mode = reasonMode(task, outcome, at);
  const choices = reasonChoices(task, outcome, at);
  const code = form.reasonCode && choices.includes(form.reasonCode) ? form.reasonCode : '';
  if (mode === 'required' && !code) problems.push({ field: 'reasonCode', key: 'io_task_problem_reason' });
  if ((mode === 'required' || mode === 'late') && code === 'other' && !form.reasonText.trim()) problems.push({ field: 'reasonText', key: 'io_task_problem_reason_text' });
  if (mode === 'early' && !form.reasonText.trim()) problems.push({ field: 'reasonText', key: 'io_task_problem_early' });
  if (needsWitness(task, outcome) && !form.witnessUserId) problems.push({ field: 'witness', key: 'io_task_problem_witness' });
  if (takesReadings(task, outcome)) {
    const { readings, invalid } = parseReadings(task.taskKind, form.readings);
    for (const field of invalid) {
      const range = readingRange(field);
      problems.push({ field, key: 'io_task_problem_range', params: { min: range.min, max: range.max } });
    }
    for (const problem of ipdReadingProblems(task.taskKind, readings)) {
      const field = readingField(problem.path);
      if (!invalid.includes(field as NumericReading)) problems.push({ field, server: problem.message });
    }
  }
  return problems;
}

export function buildRecordRequest(task: RecordableTask, form: RecordForm): RecordIpdTaskInput {
  const { outcome, at } = form;
  const mode = reasonMode(task, outcome, at);
  const choices = reasonChoices(task, outcome, at);
  const code = form.reasonCode && choices.includes(form.reasonCode) ? form.reasonCode : undefined;
  const text = form.reasonText.trim();
  const reason = text && (code === 'other' || mode === 'early') ? text : undefined;
  const doseGiven = form.doseGiven.trim();
  const site = form.site.trim();
  const notes = form.notes.trim();
  const request: RecordIpdTaskInput = { outcome };
  if (takesTime(outcome)) request.at = at.toISOString();
  if (code) request.reasonCode = code;
  if (reason) request.reason = reason;
  if (needsWitness(task, outcome) && form.witnessUserId) request.witnessUserId = form.witnessUserId;
  if (takesDose(task, outcome) && doseGiven) request.doseGiven = doseGiven;
  if (takesSite(task, outcome) && site) request.site = site;
  if (notes) request.notes = notes;
  if (takesReadings(task, outcome)) request.readings = parseReadings(task.taskKind, form.readings).readings;
  return request;
}
